import axios from "axios";
import { getLocalStorage } from "../backend/dom";
import { AuthConfig } from "../lib/AuthConfig";
import { Stage, RealmLower, LABELS } from "../constants";

interface EndpointConfig {
  apiId: string;
  region: string;
}

type ServiceEndpoints = Record<Stage, Partial<Record<RealmLower, EndpointConfig>>>;

type ServiceName = "customer-data" | "workflow-statuses" | "keymaster" | "redrive" | "scheduled-events";

// All API Gateways are deregionalized (FE-only, us-west-2).
// Each realm routes to the same single gateway per service.
const deregionalized = (beta: string, prod: string): ServiceEndpoints => ({
  beta: {
    na: { apiId: beta, region: "us-west-2" },
    fe: { apiId: beta, region: "us-west-2" },
  },
  prod: {
    na: { apiId: prod, region: "us-west-2" },
    fe: { apiId: prod, region: "us-west-2" },
    fra: { apiId: prod, region: "us-west-2" },
    zaz: { apiId: prod, region: "us-west-2" },
  },
});

const ENDPOINTS: Record<ServiceName, ServiceEndpoints> = {
  "customer-data": deregionalized("hib9118yzc", "ty6f3slini"),
  "workflow-statuses": deregionalized("23wcqlkwq8", "6nasz53ssl"),
  "keymaster": deregionalized("ggzye2zls4", "wjzndlfld1"),
  "redrive": deregionalized("69wtxhj1e8", "9awq7edb4h"),
  "scheduled-events": deregionalized("rzq5ormzz1", "tf2fkl0rug"),
};

// Map realm to the actual AWS region where data lives
const REALM_TO_REGION: Record<Stage, Record<RealmLower, string>> = {
  beta: {
    na: "us-west-2",
    fe: "us-west-2",
    fra: "us-west-2",
    zaz: "us-west-2",
  },
  prod: {
    na: "us-east-1",
    fe: "us-west-2",
    fra: "eu-central-1",
    zaz: "eu-south-2",
  },
};

export class ApiClient {
  private static instance: ApiClient;
  private stage: Stage | null = null;
  private pendingRequests = new Map<string, Promise<unknown>>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  static resetInstance(): void {
    ApiClient.instance = (undefined as unknown) as ApiClient;
  }

  async init(): Promise<void> {
    if (this.stage) {
      return;
    }
    const { data } = await axios.get("/settings.json");
    this.stage = data.stage;
  }

  getStage(): Stage {
    if (!this.stage) {
      throw new Error("ApiClient not initialized");
    }
    return this.stage;
  }

  getBaseUrl(service: ServiceName, realm: RealmLower): string {
    const stage = this.getStage();
    const config = ENDPOINTS[service][stage][realm];
    if (!config) {
      throw new Error(`No endpoint configured for ${service}/${stage}/${realm}`);
    }
    return `https://${config.apiId}.execute-api.${config.region}.amazonaws.com/prod`;
  }

  getRegionCode(realm: RealmLower): string {
    return REALM_TO_REGION[this.getStage()][realm];
  }

  private getAuthHeader(): string {
    return getLocalStorage().getItem("idToken") || "";
  }

  private async request<T>(method: "GET" | "POST" | "DELETE", url: string, data?: unknown): Promise<T> {
    await this.init();
    if (!(await AuthConfig.ensureFreshToken())) {
      throw new Error(LABELS.SESSION_EXPIRED);
    }
    const response = await axios({
      method,
      url,
      headers: {
        "authorization": this.getAuthHeader(),
        "Content-Type": "application/json",
      },
      data: data ? JSON.stringify(data) : undefined,
    });
    return response.data;
  }

  private dedupe<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }
    const promise = fn().finally(() => this.pendingRequests.delete(key));
    this.pendingRequests.set(key, promise);
    return promise;
  }

  get<T>(url: string): Promise<T> {
    return this.dedupe(url, () => this.request<T>("GET", url));
  }

  post<T>(url: string, data: unknown): Promise<T> {
    return this.request<T>("POST", url, data);
  }
}
